import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '../../hooks/useHasPermission';
import { PERMISSIONS } from '../../constants/permissions';
import type { InterventionGroupDto, TreatmentPlanDto } from '../../api/generated';
import { PatientCard } from './PatientCard';
import { AffectedGroupsPicker } from './AffectedGroupsPicker';
import { AffectedGroupsPopover } from './AffectedGroupsPopover';
import { computeAffectedGroupIds, DEFAULT_AFFECTED_GROUPS_CHOICE } from './affectedGroups';
import { Button } from '../UI/base/Button';
import EditIcon from '../../assets/icons/edit.svg?react';
import styles from './PatientList.module.css';

interface Patient {
  id: string;
  name: string;
  practitioners: string;
  tag?: string;
  deleted?: boolean;
}

interface PatientListProps {
  patients: Patient[];
  capacity: number;
  plan?: TreatmentPlanDto;
  selectedGroup?: InterventionGroupDto;
  isEditMode?: boolean;
  onCapacityChange?: (capacity: number, groupIds: string[]) => void;
  onPatientClick?: (patientId: string) => void;
  onAddPatient?: () => void;
  onRemovePatient?: (patientId: string, groupIds: string[]) => void;
  // Passed straight through to the remove popover, which needs them to identify
  // the pending removal.
  locationId?: string;
  scenarioId?: string;
}

export const PatientList = ({
  patients,
  capacity,
  plan,
  selectedGroup,
  isEditMode = false,
  onCapacityChange,
  onPatientClick,
  onAddPatient,
  onRemovePatient,
  locationId,
  scenarioId
}: PatientListProps) => {
  const { t } = useTranslation();
  const canEditPlan = useHasPermission(PERMISSIONS.YearPlanWrite);
  const canAssignPatient = useHasPermission(PERMISSIONS.PatientAssign);
  const [editCapacity, setEditCapacity] = useState(capacity);
  const [isCapacityEditing, setIsCapacityEditing] = useState(false);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [pendingCapacity, setPendingCapacity] = useState<number | null>(null);
  const [scopeChoice, setScopeChoice] = useState(DEFAULT_AFFECTED_GROUPS_CHOICE);
  const [removePopover, setRemovePopover] = useState<{
    patient: Patient;
    anchorRef: RefObject<HTMLButtonElement | null>;
  } | null>(null);
  const capacityInputRef = useRef<HTMLInputElement>(null);

  const realPatientCount = patients.length;
  // Derived rather than stored, so the groups a capacity change lands on are
  // always the ones the picker is currently showing.
  const pendingGroupIds =
    plan && selectedGroup ? computeAffectedGroupIds(plan, selectedGroup, scopeChoice) : [];

  useEffect(() => {
    if (isCapacityEditing) {
      capacityInputRef.current?.focus();
      capacityInputRef.current?.select();
    }
  }, [isCapacityEditing]);

  const handleCapacityInput = (value: number) => {
    setEditCapacity(value);
    if (value < realPatientCount) {
      setCapacityError(t('capacity-below-patients'));
      return;
    }
    setCapacityError(null);
  };

  const exitCapacityEdit = () => {
    setIsCapacityEditing(false);
    setEditCapacity(capacity);
    setCapacityError(null);
  };

  const handleCapacityCommit = () => {
    if (capacityError) {
      exitCapacityEdit();
      return;
    }
    if (editCapacity === capacity) {
      setIsCapacityEditing(false);
      return;
    }
    setIsCapacityEditing(false);
    setPendingCapacity(editCapacity);
    // Every capacity edit starts from the default scope instead of inheriting
    // the one picked for the previous edit.
    setScopeChoice(DEFAULT_AFFECTED_GROUPS_CHOICE);
  };

  const handleCapacityConfirm = () => {
    if (pendingCapacity == null || pendingGroupIds.length === 0) return;
    onCapacityChange?.(pendingCapacity, pendingGroupIds);
    setPendingCapacity(null);
  };

  const handleCapacityCancel = () => {
    setPendingCapacity(null);
    setEditCapacity(capacity);
  };

  const canEditCapacity = isEditMode && canEditPlan;
  const displayedCapacity = pendingCapacity ?? capacity;
  // Derived from the same displayed capacity as the header ratio, so the seats
  // row and the header can never disagree while a capacity change is pending.
  const availableSeats = Math.max(0, displayedCapacity - realPatientCount);

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        <h3 className={styles.header}>
          <span className={styles.headerLabel}>{t('patients-header')}</span>{' '}
          <span className={styles.headerRatio}>
            {realPatientCount} {t('out-of')}{' '}
            {canEditCapacity && isCapacityEditing ? (
              <input
                ref={capacityInputRef}
                className={`${styles.capacityInline} ${capacityError ? styles.capacityInputError : ''}`}
                type="number"
                min={realPatientCount}
                value={editCapacity}
                onChange={(e) => handleCapacityInput(Number(e.target.value))}
                onBlur={handleCapacityCommit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCapacityCommit();
                  } else if (e.key === 'Escape') {
                    exitCapacityEdit();
                  }
                }}
                onWheel={(e) => e.currentTarget.blur()}
              />
            ) : (
              <>
                <span className={styles.capacityStatic}>{displayedCapacity}</span>
                {canEditCapacity && pendingCapacity == null && (
                  <button
                    type="button"
                    className={styles.capacityEditButton}
                    onClick={() => {
                      setEditCapacity(capacity);
                      setIsCapacityEditing(true);
                    }}
                    aria-label={t('edit')}
                  >
                    <EditIcon />
                  </button>
                )}
              </>
            )}
            {pendingCapacity != null && (
              <span className={styles.capacityPendingHint}>
                {' → '}{pendingCapacity}
              </span>
            )}
          </span>
        </h3>
        {capacityError && <p className={styles.errorMessage}>{capacityError}</p>}

        {pendingCapacity != null && plan && selectedGroup && (
          <div className={styles.scopeSection}>
            <AffectedGroupsPicker
              plan={plan}
              selectedGroup={selectedGroup}
              choice={scopeChoice}
              onChange={setScopeChoice}
            />
            <div className={styles.scopeFooter}>
              <Button type="secondary" onClick={handleCapacityCancel}>
                {t('cancel')}
              </Button>
              <Button
                type="primary"
                onClick={handleCapacityConfirm}
                disabled={pendingGroupIds.length === 0}
              >
                {t('confirm')}
              </Button>
            </div>
          </div>
        )}

        {patients.map((patient) => (
          <PatientCard
            key={patient.id}
            name={patient.name}
            practitioners={patient.practitioners}
            tag={patient.tag}
            deleted={patient.deleted}
            onClick={patient.id ? () => onPatientClick?.(patient.id) : undefined}
            onRemove={
              onRemovePatient
                ? (anchorRef) => setRemovePopover({ patient, anchorRef })
                : undefined
            }
          />
        ))}

        {/* One aggregated open-seat row: shows the free capacity and starts
            the add-patient flow (the backend picks the seat, never the user). */}
        {canAssignPatient && onAddPatient && availableSeats > 0 && (
          <PatientCard
            name={t('available-seats', { count: availableSeats })}
            practitioners=""
            isAvailableSeat
            onClick={onAddPatient}
          />
        )}
      </div>

      {removePopover && plan && selectedGroup && (
        <AffectedGroupsPopover
          anchorRef={removePopover.anchorRef}
          plan={plan}
          selectedGroup={selectedGroup}
          title={t('remove-patient-title')}
          confirmLabel={t('remove-patient-confirm')}
          locationId={locationId}
          scenarioId={scenarioId}
          patientId={removePopover.patient.id}
          onClose={() => setRemovePopover(null)}
          onConfirm={(groupIds) => {
            onRemovePatient?.(removePopover.patient.id, groupIds);
            setRemovePopover(null);
          }}
        />
      )}
    </div>
  );
};
